import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Navbar } from '../components/Navbar'
import { Footer } from '../components/Footer'
import { Button } from '../components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import {Trophy, Medal, Award, Star, ArrowLeft, Zap, Crown, MapPin, TrendingUp, Users} from 'lucide-react'
import { lumi } from '../lib/lumi'
import { useAuth } from '../hooks/useAuth'
import { BRAZILIAN_STATES } from '../data/ibgeCities'
import { getBadgeForState, REGIONAL_BADGES } from '../data/regionalBadges'

export function RankingEstadual() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [topDonors, setTopDonors] = useState<any[]>([])
  const [userProfile, setUserProfile] = useState<any>(null)
  const [userRank, setUserRank] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedState, setSelectedState] = useState<string>('')
  const [totalStateDonors, setTotalStateDonors] = useState(0)
  const [totalStateDonations, setTotalStateDonations] = useState(0)

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return
      
      try {
        const { list: userProfiles } = await lumi.entities.profiles.list({
          filter: { userId: user.userId }
        })
        if (userProfiles.length > 0) {
          const profile = userProfiles[0]
          setUserProfile(profile)
          setSelectedState(profile.state || '')
        }
      } catch (error) {
        console.error('Failed to fetch user profile:', error)
      }
    }
    fetchUserProfile()
  }, [user])

  useEffect(() => {
    const fetchStateRanking = async () => {
      if (!selectedState) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        
        const { list, total } = await lumi.entities.profiles.list({
          filter: { 
            isDonor: true,
            state: selectedState
          },
          sort: { points: -1 },
          limit: 50 // Reduzido de 100 para 50 para performance
        })
        
        setTotalStateDonors(total)
        setTotalStateDonations(list.reduce((sum, d) => sum + (d.totalDonations || 0), 0))
        setTopDonors(list.slice(0, 20))
        
        if (userProfile) {
          const userPosition = list.findIndex(d => d._id === userProfile._id)
          setUserRank(userPosition !== -1 ? userPosition + 1 : null)
        }
      } catch (error) {
        console.error('Failed to fetch state ranking:', error)
      } finally {
        setLoading(false)
      }
    }
    
    fetchStateRanking()
  }, [selectedState, userProfile])

  const getRankIcon = (index: number) => {
    if (index === 0) return <Crown className="h-7 w-7 text-yellow-500" />
    if (index === 1) return <Medal className="h-6 w-6 text-gray-400" />
    if (index === 2) return <Award className="h-6 w-6 text-orange-600" />
    if (index <= 4) return <Star className="h-5 w-5 text-yellow-500" />
    return <Star className="h-5 w-5 text-gray-400" />
  }

  const getRankBg = (index: number) => {
    if (index === 0) return 'bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-400 border-2'
    if (index === 1) return 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-400 border-2'
    if (index === 2) return 'bg-gradient-to-r from-orange-50 to-orange-100 border-orange-400 border-2'
    if (index <= 4) return 'bg-gradient-to-r from-blue-50 to-blue-100 border-blue-300'
    if (index <= 9) return 'bg-gradient-to-r from-purple-50 to-purple-100 border-purple-300'
    return 'bg-white border-gray-200'
  }

  const getRankBadge = (index: number) => {
    if (index === 0) return <span className="ml-2 px-2 py-1 bg-yellow-500 text-white text-xs font-bold rounded-full">🥇 1º do Estado</span>
    if (index <= 4) return <span className="ml-2 px-2 py-1 bg-blue-500 text-white text-xs font-bold rounded-full">⭐ Top 5</span>
    if (index <= 9) return <span className="ml-2 px-2 py-1 bg-purple-500 text-white text-xs font-bold rounded-full">🏆 Top 10</span>
    return null
  }

  const stateName = BRAZILIAN_STATES.find(s => s.code === selectedState)?.name || selectedState

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/ranking')}
          className="mb-4"
          aria-label="Voltar para rankings"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center shadow-md">
              <Trophy className="h-10 w-10 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-black text-gray-900 mb-2">
            🌟 Campeões de {stateName}
          </h1>
          <p className="text-gray-600 text-lg">
            Top 20 doadores do estado
          </p>
          
          {/* Seletor de Estado */}
          <div className="mt-4 max-w-md mx-auto">
            <select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-medium"
              aria-label="Selecionar estado"
            >
              <option value="">Selecione um estado</option>
              {BRAZILIAN_STATES.map((state) => (
                <option key={state.code} value={state.code}>
                  {state.name} ({state.code})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* User Position Card */}
        {userProfile && selectedState && userRank && (
          <Card className="mb-8 border-2 border-purple-500 bg-gradient-to-r from-purple-50 to-white shadow-lg">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-white font-black text-xl shadow-md">
                    {userProfile.displayName?.charAt(0) || 'U'}
                  </div>
                  <div>
                    <h3 className="font-bold text-xl text-gray-900 flex items-center">
                      {userProfile.displayName}
                      {userRank <= 10 && (
                        <span className="ml-2 px-2 py-1 bg-purple-500 text-white text-xs font-bold rounded-full">
                          Top 10 Estadual
                        </span>
                      )}
                    </h3>
                    <p className="text-sm text-gray-600">{userProfile.bloodType} • {userProfile.city}/{userProfile.state}</p>
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-black text-purple-600">#{userRank}</div>
                  <p className="text-xs text-gray-600 font-medium">em {stateName}</p>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 border border-purple-200">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="flex items-center justify-center space-x-1 mb-1">
                      <Zap className="h-4 w-4 text-yellow-500" />
                      <span className="text-xl font-black text-gray-900">{userProfile.points || 0}</span>
                    </div>
                    <p className="text-xs text-gray-600">pontos</p>
                  </div>
                  <div>
                    <p className="text-xl font-black text-purple-600">{userProfile.totalDonations || 0}</p>
                    <p className="text-xs text-gray-600">doações</p>
                  </div>
                  <div>
                    <p className="text-xl font-black text-purple-600">{(userProfile.totalDonations || 0) * 4}</p>
                    <p className="text-xs text-gray-600">vidas salvas</p>
                  </div>
                </div>
              </div>

              {userProfile.badges && userProfile.badges.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-bold text-gray-700 mb-2">Suas Medalhas Estaduais:</p>
                  <div className="flex flex-wrap gap-2">
                    {userProfile.badges
                      .filter((b: string) => REGIONAL_BADGES[b]?.scope === 'state')
                      .map((badge: string) => {
                        const badgeInfo = getBadgeForState(badge, stateName)
                        return (
                          <span key={badge} className="px-3 py-1 bg-white rounded-full text-sm font-medium border border-purple-200 shadow-sm">
                            {badgeInfo.icon} {badgeInfo.name}
                          </span>
                        )
                      })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* State Stats */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
            <CardContent className="pt-6 text-center">
              <Users className="h-8 w-8 text-blue-600 mx-auto mb-2" />
              <p className="text-3xl font-black text-gray-900">{totalStateDonors}</p>
              <p className="text-sm text-gray-600">doadores em {stateName}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-white border-green-200">
            <CardContent className="pt-6 text-center">
              <TrendingUp className="h-8 w-8 text-green-600 mx-auto mb-2" />
              <p className="text-3xl font-black text-gray-900">{totalStateDonations}</p>
              <p className="text-sm text-gray-600">doações totais</p>
            </CardContent>
          </Card>
        </div>

        {/* Top 20 State Ranking */}
        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl font-black flex items-center">
              <Trophy className="h-6 w-6 text-purple-500 mr-2" />
              Top 20 de {stateName}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-20 bg-gray-100 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : topDonors.length > 0 ? (
              <div className="space-y-3">
                {topDonors.map((donor, index) => (
                  <div 
                    key={donor._id} 
                    className={`flex items-center justify-between p-4 rounded-xl border transition-all hover:shadow-md ${getRankBg(index)}`}
                  >
                    <div className="flex items-center space-x-4 flex-1">
                      <div className="flex-shrink-0">
                        {getRankIcon(index)}
                      </div>
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-white font-bold text-lg shadow-md">
                        {donor.displayName?.charAt(0) || 'D'}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center">
                          <h4 className="font-bold text-gray-900">{donor.displayName}</h4>
                          {getRankBadge(index)}
                        </div>
                        <p className="text-sm text-gray-600">
                          {donor.bloodType} • {donor.city}/{donor.state} • {donor.totalDonations || 0} {(donor.totalDonations || 0) === 1 ? 'doação' : 'doações'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center justify-end space-x-1 mb-1">
                        <Zap className="h-4 w-4 text-yellow-500" />
                        <span className="text-xl font-black text-gray-900">{donor.points || 0}</span>
                      </div>
                      <p className="text-xs text-gray-600 font-medium">pontos</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <MapPin className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Seja o primeiro campeão de {stateName}!</h3>
                <p className="text-gray-600 mb-4">Confirme uma doação e entre para o ranking estadual</p>
                <Button onClick={() => navigate('/solicitacoes')} className="mt-2">
                  Ver Pedidos de Doação
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="mt-8 text-center">
          <Button 
            onClick={() => navigate('/solicitacoes')}
            className="w-full max-w-md h-14 text-lg font-bold bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
          >
            Ver Pedidos e Doar Agora
          </Button>
        </div>
      </div>
      <Footer />
    </div>
  )
}
